import { useEffect, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Pin } from 'lucide-react';
import { canAccess, capabilitiesIn } from '../lib/access.js';
import {
  ApiError,
  api,
  type MixAdjustedBaseline,
  type MixAdjustedFigure,
  type MixAdjustedReport,
  type MixShiftRow,
} from '../lib/api.js';
import { dateOnly, dateTime, minutes, num, pct, personLabel, sharePct } from '../lib/format.js';
import {
  AccessDenied,
  Badge,
  Button,
  Callout,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Panel,
  Select,
  Spinner,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Textarea,
  cn,
  useToast,
} from '../ui/index.js';

interface Ctx {
  tenant: string;
  role: string | null;
  tenantName: string | undefined;
  /** /me, for this desk's capabilities (a desk may change who sees reports). */
  me?: { tenants: { id: string; capabilities?: string[] }[] };
}

const MONTH_OPTIONS = [
  { value: '3', label: 'Last 3 months' },
  { value: '6', label: 'Last 6 months' },
  { value: '12', label: 'Last 12 months' },
  { value: '24', label: 'Last 24 months' },
];

/**
 * Mix-adjusted desk performance (FR-RPT-09..11).
 *
 * The cardinal rule of this screen: never show the crude figure alone, and
 * never show the adjusted figure alone. Crude, adjusted and the mix shift
 * belong together, because any one of them in isolation says something
 * false — and the crude figure is the one people quote alone, which invites
 * exactly the wrong conclusion about the deflection programme.
 */
export function MixAdjustedPage() {
  const { tenant, role, tenantName, me } = useOutletContext<Ctx>();
  // report.tenant — the same threshold the rest of the reports screen uses,
  // so a link into this page never opens onto an AccessDenied a moment after
  // the nav item that pointed here was itself gated on the same check.
  const allowed = canAccess(role, 'dashboard', capabilitiesIn(me, tenant));
  // config.write — pinning changes what every historic comparison means, so
  // it gets the tenant-admin bar the Configuration screen uses, not the
  // report-reading one.
  const canPin = canAccess(role, 'admin');

  const [params, setParams] = useSearchParams();
  const months = params.get('months') ?? '6';
  const [pinOpen, setPinOpen] = useState(false);
  const qc = useQueryClient();

  const report = useQuery({
    queryKey: ['mix-adjusted', tenant, months],
    queryFn: () =>
      api.get<MixAdjustedReport>(
        `/api/v1/tenants/${tenant}/reports/mix-adjusted?months=${months}`,
      ),
    enabled: Boolean(tenant) && allowed,
  });

  if (!allowed) {
    return <AccessDenied area="dashboard" role={role} tenantName={tenantName} />;
  }

  const data = report.data;
  const latest = data?.periods.at(-1);

  return (
    <div className="mx-auto max-w-[1180px] p-4 sm:p-6">
      <PageHeader
        title="Mix-adjusted desk performance"
        description="Crude, adjusted and the mix shift, together — a raw trend across a changing mix is uninterpretable in both directions."
        actions={
          <div className="flex items-center gap-2">
            <Select
              value={months}
              onValueChange={(v) =>
                setParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('months', v);
                  return next;
                }, { replace: true })
              }
              options={MONTH_OPTIONS}
              size="sm"
              aria-label="Reporting window"
              className="w-40"
            />
            {canPin && (
              <Button size="sm" leading={<Pin className="h-3.5 w-3.5" />} onClick={() => setPinOpen(true)}>
                {data?.baseline ? 'Re-baseline' : 'Pin baseline'}
              </Button>
            )}
          </div>
        }
      />

      {report.error && <ErrorState error={report.error} className="mb-4" />}
      {report.isLoading && (
        <div className="flex justify-center py-16">
          <Spinner label="Loading" />
        </div>
      )}

      {data && (
        <div className="space-y-4">
          {!data.baseline && data.note && (
            <Callout tone="warn" title="No baseline is pinned" icon={<AlertTriangle />}>
              <p>{data.note}</p>
              {!canPin && (
                <p className="mt-1.5 text-2xs">
                  Ask a group admin to pin one — this identity can read the report but not set the
                  baseline it is measured against.
                </p>
              )}
            </Callout>
          )}

          <Panel
            title="Pinned baseline"
            subtitle="FR-RPT-11 — the mix every adjusted figure below is standardised to"
          >
            {data.baseline ? (
              <BaselineSummary baseline={data.baseline} />
            ) : (
              <p className="text-xs text-ink-tertiary">
                No baseline has ever been pinned for this group. Every adjusted column below is
                absent for that reason, not because nothing could be computed.
              </p>
            )}
          </Panel>

          <Panel
            title="Crude vs adjusted, by month"
            subtitle="Resolution in minutes, over work a person did. SLA attainment as a rate, same population."
          >
            {data.periods.length === 0 ? (
              <p className="py-6 text-center text-xs text-ink-tertiary">
                No tickets in this window.
              </p>
            ) : (
              <Table minWidth={760}>
                <THead>
                  <TR>
                    <TH>Period</TH>
                    <TH numeric>Demand</TH>
                    <TH>Deflected</TH>
                    <TH>Resolution — crude → adjusted</TH>
                    <TH>SLA attainment — crude → adjusted</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.periods.map((p) => (
                    <TR key={p.period}>
                      <TD>
                        <span className="font-medium text-ink">{p.period}</span>
                      </TD>
                      <TD numeric>{num(p.demand)}</TD>
                      <TD>
                        <span className="whitespace-nowrap text-xs">
                          {num(p.deflected)} of {num(p.demand)} ({sharePct(p.deflectedShare)})
                        </span>
                      </TD>
                      <TD>
                        <AdjustedCell
                          figure={p.resolution}
                          format={minutes}
                          baselinePinned={Boolean(data.baseline)}
                        />
                      </TD>
                      <TD>
                        <AdjustedCell
                          figure={p.attainment}
                          format={(v) => pct(v)}
                          baselinePinned={Boolean(data.baseline)}
                        />
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </Panel>

          {latest && (
            <Panel
              title={`Mix shift — ${latest.period}`}
              subtitle="Baseline share versus current share, for the request types that moved most"
              footer={
                <p className="text-2xs leading-relaxed text-ink-tertiary">
                  Adjusted figures above are standardised to this pinned mix, not to the current
                  one — the whole point is comparing this period against what the desk used to
                  face, not against itself. Resolution is measured over work a person did;
                  deflected share and the mix itself are measured over all demand, assistant
                  soft closes included, because the mix shift IS the deflection. Soft closes are
                  excluded from desk performance deliberately: a ticket the assistant answered
                  took no desk time, and folding it into a median would report the desk as faster
                  the day the assistant was switched on rather than as seeing less of the work.
                </p>
              }
            >
              {latest.mix.length === 0 ? (
                <p className="py-4 text-center text-xs text-ink-tertiary">No demand this period.</p>
              ) : (
                <ul className="space-y-1">
                  {latest.mix.map((row) => (
                    <MixShiftListRow key={row.key} row={row} />
                  ))}
                </ul>
              )}
            </Panel>
          )}
        </div>
      )}

      <Dialog open={pinOpen} onOpenChange={setPinOpen}>
        <PinBaselineDialogContent
          tenant={tenant}
          current={data?.baseline ?? null}
          open={pinOpen}
          onPinned={() => {
            setPinOpen(false);
            void qc.invalidateQueries({ queryKey: ['mix-adjusted', tenant] });
          }}
        />
      </Dialog>
    </div>
  );
}

function BaselineSummary({ baseline }: { baseline: MixAdjustedBaseline }) {
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-ink">{baseline.label}</span>
        <Badge tone="brand">
          {dateOnly(baseline.window_from)} – {dateOnly(baseline.window_to)}
        </Badge>
        <Badge tone="neutral">{num(baseline.sample_size)} tickets</Badge>
      </div>
      <p className="text-xs text-ink-secondary">
        Pinned {dateTime(baseline.pinned_at)}
        {baseline.pinned_by_name ? ` by ${personLabel(baseline.pinned_by_name, null)}` : ''}
      </p>
      {baseline.note && <p className="text-xs text-ink-tertiary">{baseline.note}</p>}
    </div>
  );
}

/**
 * One crude/adjusted pair. `adjusted === null` is never rendered as a bare
 * dash: it either means no baseline is pinned, or the baseline has nothing
 * to say about this period's mix, and each of those is a different fact a
 * reader needs in order not to mistake absence for zero.
 */
function AdjustedCell({
  figure,
  format,
  baselinePinned,
}: {
  figure: MixAdjustedFigure;
  format: (v: number) => string;
  baselinePinned: boolean;
}) {
  if (figure.crude === null) {
    return <span className="text-xs text-ink-tertiary">no resolved work this period</span>;
  }

  if (!baselinePinned) {
    return (
      <div>
        <span className="nums text-sm font-medium text-ink">{format(figure.crude)}</span>
        <p className="mt-0.5 text-2xs text-ink-tertiary">adjusted: no baseline pinned</p>
      </div>
    );
  }

  if (figure.adjusted === null) {
    const reason =
      figure.missingStrata.length > 0
        ? `${namedStrata(figure.missingStrata)} absent this period`
        : 'no baseline coverage';
    return (
      <div>
        <span className="nums text-sm font-medium text-ink">{format(figure.crude)}</span>
        <p className="mt-0.5 text-2xs text-danger">adjusted: {reason}</p>
      </div>
    );
  }

  const delta = figure.adjusted - figure.crude;
  // Below ~0.8 coverage the adjustment is interpolating more than adjusting
  // (see the service comment on `baselineCoverage`) — a warning, not a hard
  // stop, because the figure is still the best available one.
  const lowCoverage = figure.baselineCoverage < 0.8;
  const material = Math.abs(delta) / Math.max(Math.abs(figure.crude), 1e-9) > 0.2;

  return (
    <div>
      <div className="flex items-baseline gap-1.5">
        <span className="nums text-sm text-ink-secondary">{format(figure.crude)}</span>
        <span aria-hidden className="text-ink-tertiary">
          →
        </span>
        <span className={cn('nums text-sm font-semibold', material ? 'text-warn' : 'text-ink')}>
          {format(figure.adjusted)}
        </span>
      </div>
      {lowCoverage && (
        <p className="mt-0.5 flex items-center gap-1 text-2xs text-warn">
          <AlertTriangle aria-hidden className="h-3 w-3 shrink-0" />
          coverage {pct(figure.baselineCoverage)}
          {figure.missingStrata.length > 0 && ` — ${namedStrata(figure.missingStrata)} thin or absent`}
        </p>
      )}
    </div>
  );
}

function MixShiftListRow({ row }: { row: MixShiftRow }) {
  const deltaPp = row.delta * 100;
  const material = Math.abs(deltaPp) >= 10;

  return (
    <li className="grid grid-cols-[minmax(0,9rem)_1fr_auto] items-center gap-3 py-1">
      <span className="truncate text-xs text-ink-secondary" title={row.key}>
        {row.key}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="relative h-2 flex-1 overflow-hidden rounded-full bg-surface-sunken" title="Baseline share">
          <span
            className="absolute inset-y-0 left-0 rounded-full bg-ink-tertiary/50"
            style={{ width: `${Math.min(100, row.baselineShare * 100)}%` }}
          />
        </span>
        <span className="relative h-2 flex-1 overflow-hidden rounded-full bg-surface-sunken" title="Current share">
          {/* A Tailwind token, not `var(--c1)`. That variable is real but is
              declared on the chart container in `components/charts.tsx`, so
              outside a chart it resolves to nothing: the declaration was
              invalid and every current-share track rendered empty while the
              numbers beside it were correct. */}
          <span
            className="absolute inset-y-0 left-0 rounded-full bg-brand"
            style={{ width: `${Math.min(100, row.currentShare * 100)}%` }}
          />
        </span>
      </span>
      <span className="nums flex items-center gap-1.5 whitespace-nowrap text-xs">
        {sharePct(row.baselineShare)} → {sharePct(row.currentShare)}
        <Badge tone={material ? 'warn' : 'neutral'}>
          {deltaPp >= 0 ? '+' : ''}
          {deltaPp.toFixed(1)}pp
        </Badge>
      </span>
    </li>
  );
}

/**
 * Pinning a baseline (FR-RPT-11).
 *
 * Deliberately not a one-click action: the moment the baseline moves, every
 * historic comparison on this screen changes meaning, so the dialog says so
 * before it lets the submit through, and the previous baseline is retired —
 * never deleted — so an old figure stays reproducible.
 */
function PinBaselineDialogContent({
  tenant,
  current,
  open,
  onPinned,
}: {
  tenant: string;
  current: MixAdjustedBaseline | null;
  open: boolean;
  onPinned: () => void;
}) {
  const [label, setLabel] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const toast = useToast();

  // The dialog is never unmounted (its trigger buttons live in two places on
  // the page), so a cancelled edit would otherwise still be sitting in these
  // fields the next time it opens — reset on the way in, not just on success.
  useEffect(() => {
    if (!open) return;
    setLabel('');
    setFrom('');
    setTo('');
    setNote('');
  }, [open]);

  const pin = useMutation({
    mutationFn: () =>
      api.post<MixAdjustedBaseline>(`/api/v1/tenants/${tenant}/reports/baseline`, {
        label: label.trim(),
        from,
        to,
        note: note.trim() ? note.trim() : undefined,
      }),
    onSuccess: (baseline) => {
      toast.success(
        `${baseline.label} pinned`,
        'Every adjusted figure now reads against this window.',
      );
      setLabel('');
      setFrom('');
      setTo('');
      setNote('');
      onPinned();
    },
    onError: (err: unknown) => {
      toast.error(err instanceof ApiError ? err.message : 'The baseline could not be pinned');
    },
  });

  const labelOk = label.trim().length >= 3;
  const datesOk = from.length > 0 && to.length > 0 && from <= to;

  return (
    <DialogContent
      title={current ? 'Re-baseline' : 'Pin a baseline'}
      description="A window before deflection started — or before whatever change you want every future period compared against."
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          pin.mutate();
        }}
      >
        <div className="space-y-3">
          {current && (
            <Callout tone="warn" icon={<AlertTriangle />}>
              Re-baselining changes the meaning of every historic comparison on this screen. The
              current baseline, &ldquo;{current.label}&rdquo;, is retired rather than deleted — it
              stays reproducible, but nothing here will compare against it after this.
            </Callout>
          )}

          <Field label="Label" required hint="What a reader will see this baseline called.">
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Before the assistant went live"
              autoFocus
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="From" required>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label="To" required>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
          </div>

          <Field label="Note" hint="Why this window — optional, but the next reader will ask.">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          </Field>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="ghost">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" loading={pin.isPending} disabled={!labelOk || !datesOk}>
            {current ? 'Retire and pin new baseline' : 'Pin baseline'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
/**
 * Name a few of the absent strata, not all of them.
 *
 * Thirty request types joined with commas filled a table cell and pushed the
 * whole row sideways — a warning nobody can read is not a warning. The count
 * is what matters at a glance; three examples make it concrete.
 */
function namedStrata(keys: readonly string[]): string {
  const shown = keys.slice(0, 3).join(', ');
  const rest = keys.length - 3;
  return rest > 0 ? `${shown} and ${rest} more` : shown;
}
